import {useEffect, useRef, useState} from 'react';

/**
 * A hook that takes a function, and returns a value-stable version of that same
 * function. Similar to `useCallback`, but does not require the caller to manage
 * a dependency array. The tradeoff is that changes to the function will not
 * will not cause re-renders in the consuming component.
 *
 * @param fn
 */
export function useMutableFn<Fn extends (...args: any[]) => any>(fn: Fn): Fn {
  const ref = useRef(fn);
  useEffect(() => {
    ref.current = fn;
  }, [fn]);

  // Using state here to demand stable identity for the call function
  // throughout the entire lifecycle of the containing component.
  // Double fn here to work around useState lazy initialization:
  const [call] = useState(() => (...args: Parameters<Fn>) => {
    return ref.current(...args) as ReturnType<Fn>;
  });

  // @ts-expect-error -- TS can't verify that the types are the same here
  return call;
}
